import {useState} from 'react';
import {Check} from 'lucide-react';
import {PhoneHQLoader, PhoneHQButton} from 'ch3-ui-lib';
import {useAppDispatch} from '../../../store/store';
import {showToast} from '../../../store/features/Notifications/notificationsSlice';
import {
    type PhoneNumberType,
    useCreateWebhookMutation,
    useGetWebhookQuery,
    useUpdateWebhookMutation,
    type Webhook,
    type WebhookEventType,
} from '../../../store/features/integrations/integrationsService';
import {useFetchMsisdnsAdminQuery} from '../../../store/features/MsisdnSettings/msisdnSettingsService';
import type {Msisdn} from '../../../store/features/Msisdns/msisdnService';
import {getFlag, getFormatedPhoneNumber} from '../../../utilis/msisdnUtilis';
import {errorStatus, EVENT_OPTIONS} from './webhookModel';
import {CloseButton, Drawer, RadioCardList} from './webhookUi';

const URL_PATTERN = /^https?:\/\/[^\s./]+\.\S+/;
const INPUT = 'h-[38px] w-full rounded-md border border-line px-3 text-sm text-ink outline-none placeholder:text-ink-disabled focus:border-indigo-500 focus:ring-[3px] focus:ring-indigo-500/15';
const SCOPE_OPTIONS = [
    {value: 'ALL', label: 'All phone numbers', description: 'Includes numbers added later.'},
    {value: 'SOME', label: 'Selected numbers', description: 'Only events from the numbers you choose.'},
] as const;

type EventValue = (typeof EVENT_OPTIONS)[number]['value'];

const isEventValue = (type: string): type is EventValue => EVENT_OPTIONS.some(option => option.value === type);

interface FormValues {
    readonly name: string;
    readonly url: string;
    readonly event: EventValue | undefined;
    readonly scope: PhoneNumberType;
    readonly numbers: readonly number[];
}

const NEW_WEBHOOK: FormValues = {name: '', url: '', event: 'CALL_ENDED', scope: 'ALL', numbers: []};

const valuesOf = (webhook: Webhook): FormValues => {
    const eventType = webhook.eventTypes.find(item => isEventValue(item.type));
    return {
        name: webhook.name,
        url: webhook.url,
        event: eventType && isEventValue(eventType.type) ? eventType.type : undefined,
        scope: eventType?.phoneNumbers?.type ?? 'ALL',
        numbers: eventType?.phoneNumbers?.phoneNumberIds ?? [],
    };
};

export function WebhookFormDrawer({webhookId, onClose}: {readonly webhookId: number | 'new'; readonly onClose: () => void}) {
    const editing = webhookId !== 'new';
    const {data: webhook, isError} = useGetWebhookQuery({id: Number(webhookId)}, {skip: !editing});
    const {data: numbers} = useFetchMsisdnsAdminQuery({phrase: ''});
    const title = editing ? 'Edit webhook' : 'New webhook';

    return <Drawer label={title} onClose={onClose}>
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
            <CloseButton onClose={onClose}/>
        </div>
        {isError ? <p role="alert" className="px-5 py-10 text-center text-sm text-danger-ink">This webhook could not be loaded.</p>
            : editing && !webhook ? <div className="flex justify-center py-10"><PhoneHQLoader/></div>
                : <WebhookForm key={webhook?.id ?? 'new'} webhookId={editing ? Number(webhookId) : undefined}
                    initial={webhook ? valuesOf(webhook) : NEW_WEBHOOK} phoneNumbers={numbers?.data ?? []} onClose={onClose}/>}
    </Drawer>;
}

interface WebhookFormProps {
    readonly webhookId: number | undefined;
    readonly initial: FormValues;
    readonly phoneNumbers: readonly Msisdn[];
    readonly onClose: () => void;
}

function WebhookForm({webhookId, initial, phoneNumbers, onClose}: WebhookFormProps) {
    const dispatch = useAppDispatch();
    const [values, setValues] = useState(initial);
    const [nameError, setNameError] = useState('');
    const [urlError, setUrlError] = useState('');
    const [createWebhook, {isLoading: creating}] = useCreateWebhookMutation();
    const [updateWebhook, {isLoading: updating}] = useUpdateWebhookMutation();
    const update = (patch: Partial<FormValues>) => setValues(current => ({...current, ...patch}));

    const url = values.url.trim();
    const urlInvalid = url !== '' && !URL_PATTERN.test(url);
    const valid = values.name.trim() !== '' && URL_PATTERN.test(url) && values.event !== undefined && (values.scope === 'ALL' || values.numbers.length > 0);
    const saving = creating || updating;

    const save = async () => {
        if (!valid || !values.event) return;
        const eventTypes: WebhookEventType[] = [{
            type: values.event,
            phoneNumbers: values.scope === 'ALL' ? {type: 'ALL'} : {type: 'SOME', phoneNumberIds: [...values.numbers]},
        }];
        const data = {name: values.name.trim(), url, eventTypes};
        try {
            if (webhookId === undefined) {
                await createWebhook({data: {...data, status: 'ENABLED', technical: false}}).unwrap();
                dispatch(showToast({description: 'Webhook created. Send a test event to check it.', type: 'success'}));
            } else {
                await updateWebhook({id: webhookId, data}).unwrap();
                dispatch(showToast({description: 'Changes saved', type: 'success'}));
            }
            onClose();
        } catch (error) {
            const status = errorStatus(error);
            if (status === 409) setNameError('A webhook with this name already exists.');
            else if (status === 422) setUrlError('This URL can’t receive webhooks. Use a public http(s) address.');
            else dispatch(showToast({description: webhookId === undefined ? 'Webhook could not be created' : 'Changes could not be saved', type: 'error'}));
        }
    };

    const toggleNumber = (id: number) =>
        update({numbers: values.numbers.includes(id) ? values.numbers.filter(item => item !== id) : [...values.numbers, id]});

    return <>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-5 pb-8 pt-5">
            <div className="flex flex-col gap-4">
                <label className="flex flex-col gap-1.5">
                    <span className="text-[13.5px] font-medium text-ink">Name</span>
                    <input phq-id="addWebhook-webhookNameInput" autoFocus value={values.name} placeholder="e.g. CRM call sync" aria-invalid={nameError !== ''}
                        className={INPUT} onChange={event => { update({name: event.target.value}); setNameError(''); }}/>
                    {nameError && <span role="alert" className="text-[12.5px] text-danger-ink">{nameError}</span>}
                </label>
                <label className="flex flex-col gap-1.5">
                    <span className="text-[13.5px] font-medium text-ink">Endpoint URL</span>
                    <input phq-id="addWebhook-urlInput" value={values.url} placeholder="https://" inputMode="url" aria-invalid={urlInvalid || urlError !== ''}
                        className={`${INPUT} font-mono text-[13px]`} onChange={event => { update({url: event.target.value}); setUrlError(''); }}/>
                    <span className={`text-[12.5px] ${urlInvalid || urlError ? 'text-danger-ink' : 'text-ink-muted'}`}>
                        {urlError || (urlInvalid ? 'Enter a full URL, e.g. https://example.com/hooks' : 'We send a POST request with a JSON body to this URL.')}
                    </span>
                </label>
            </div>

            <div className="flex flex-col gap-2.5">
                <h3 className="text-[15px] font-semibold text-ink">Event</h3>
                <RadioCardList label="Event" options={EVENT_OPTIONS} value={values.event} onChange={event => update({event})}/>
            </div>

            <div className="flex flex-col gap-2.5">
                <h3 className="text-[15px] font-semibold text-ink">Phone numbers</h3>
                <RadioCardList label="Phone numbers" options={SCOPE_OPTIONS} value={values.scope} onChange={scope => update({scope})}/>
                {values.scope === 'SOME' && <div role="group" aria-label="Selected numbers" className="flex max-h-[320px] flex-col overflow-y-auto rounded-[10px] border border-line">
                    {phoneNumbers.length === 0 && <p className="px-3.5 py-3 text-[13px] text-ink-muted">No phone numbers yet.</p>}
                    {phoneNumbers.map((number, index) => {
                        const checked = values.numbers.includes(number.msisdnId);
                        return <button key={number.msisdnId} type="button" role="checkbox" aria-checked={checked} onClick={() => toggleNumber(number.msisdnId)}
                            className={`box-border flex w-full items-center gap-3 px-3.5 py-2.5 text-left hover:bg-zinc-50 ${index > 0 ? 'border-t border-line' : ''}`}>
                            <span aria-hidden className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded border-[1.5px] ${checked ? 'border-indigo-500 bg-indigo-500' : 'border-line bg-surface'}`}>
                                {checked && <Check size={12} strokeWidth={3} className="!p-0 !text-white"/>}
                            </span>
                            <span className="rounded border border-line px-1.5 py-0.5 text-[11px] font-semibold uppercase text-ink-muted">{getFlag(number.msisdn) || '—'}</span>
                            <span className="text-[13.5px] tabular-nums text-ink">{getFormatedPhoneNumber(number.msisdn)}</span>
                            <span className="ml-auto truncate text-[13px] text-ink-muted">{number.label}</span>
                        </button>;
                    })}
                </div>}
            </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-line bg-zinc-50 px-5 py-3.5">
            <PhoneHQButton variant="secondary" size="sm" onClick={onClose}>Cancel</PhoneHQButton>
            <PhoneHQButton phq-id="addWebhook-addNewBtn" variant="primary" size="sm" disabled={!valid || saving} onClick={() => void save()}>
                {saving ? 'Saving…' : webhookId === undefined ? 'Create webhook' : 'Save changes'}
            </PhoneHQButton>
        </div>
    </>;
}
